import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

function Tabs({ tabs, active, onSet }) {
  return (
    <div className="flex gap-2 mb-5 flex-wrap">
      {tabs.map(t => (
        <button key={t.id} onClick={() => onSet(t.id)}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${active === t.id ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function AgeCalc() {
  const [tab, setTab] = useState('age');
  return (
    <CalcShell look="cnet"
      title="Age Calculator"
      subtitle="Compute age in years, months, days and find the difference between two dates."
      about={<>
        <p>The <b>Age Calculator</b> determines age based on the date of birth and a reference date. The result is expressed in years, months, days, and further into weeks, hours, minutes, and seconds.</p>
        <p>Two people born on the same day may be considered a different age by a few hours because the moment of birth actually differs, but this calculator uses date-only accuracy.</p>
      </>}
    >
      <Tabs
        active={tab}
        onSet={setTab}
        tabs={[
          { id: 'age', label: 'Age Calculator' },
          { id: 'diff', label: 'Days Between Dates' }
        ]}
      />
      {tab === 'age' ? <AgeMode /> : <DiffMode />}
    </CalcShell>
  );
}

function AgeMode() {
  const [dob, setDob] = useState('1995-05-15');
  const [today, setToday] = useState(() => new Date().toISOString().slice(0, 10));
  const c = useMemo(() => {
    const b = new Date(dob), t = new Date(today);
    if (b > t) return null;
    let y = t.getFullYear() - b.getFullYear();
    let m = t.getMonth() - b.getMonth();
    let d = t.getDate() - b.getDate();
    if (d < 0) { m--; d += new Date(t.getFullYear(), t.getMonth(), 0).getDate(); }
    if (m < 0) { y--; m += 12; }
    const totalDays = Math.floor((t - b) / 86400000);
    return { y, m, d, totalDays, weeks: Math.floor(totalDays/7), hours: totalDays*24, mins: totalDays*24*60, secs: totalDays*24*3600 };
  }, [dob, today]);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Date of Birth</label><input type="date" value={dob} onChange={e => setDob(e.target.value)} /></div>
          <div className="field"><label>Age at the date of</label><input type="date" value={today} onChange={e => setToday(e.target.value)} /></div>
        </div>
        <div>
          {c ? (<>
            <div className="result-panel mb-4"><div className="result-title">Age</div><div className="result-value">{c.y} <span className="text-lg">yrs</span> {c.m} <span className="text-lg">mo</span> {c.d} <span className="text-lg">d</span></div></div>
            <table className="data-table"><tbody>
              <tr><td>Total months</td><td className="text-right">{c.y*12 + c.m}</td></tr>
              <tr><td>Total weeks</td><td className="text-right">{c.weeks.toLocaleString()}</td></tr>
              <tr><td>Total days</td><td className="text-right">{c.totalDays.toLocaleString()}</td></tr>
              <tr><td>Total hours</td><td className="text-right">{c.hours.toLocaleString()}</td></tr>
              <tr><td>Total minutes</td><td className="text-right">{c.mins.toLocaleString()}</td></tr>
              <tr><td>Total seconds</td><td className="text-right">{c.secs.toLocaleString()}</td></tr>
            </tbody></table>
          </>) : <div className="text-red-600">Birth date must be before target date</div>}
        </div>
      </div>
      {c && <StepsPanel steps={[
        `Compute year difference and adjust for months and days`,
        `Age = <b>${c.y} years, ${c.m} months, ${c.d} days</b>`,
        `Total days elapsed = <b>${c.totalDays.toLocaleString()}</b>`
      ]} />}
    </>
  );
}

function DiffMode() {
  const [start, setStart] = useState('2020-01-01');
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const c = useMemo(() => {
    const s = new Date(start), e = new Date(end);
    const days = Math.round((e - s) / 86400000);
    return { days, weeks: (days/7).toFixed(2), months: (days/30.44).toFixed(2), years: (days/365.25).toFixed(3) };
  }, [start, end]);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Start Date</label><input type="date" value={start} onChange={e => setStart(e.target.value)} /></div>
          <div className="field"><label>End Date</label><input type="date" value={end} onChange={e => setEnd(e.target.value)} /></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Total Days</div><div className="result-value">{c.days.toLocaleString()}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Weeks</td><td className="text-right">{c.weeks}</td></tr>
            <tr><td>Months</td><td className="text-right">{c.months}</td></tr>
            <tr><td>Years</td><td className="text-right">{c.years}</td></tr>
          </tbody></table>
        </div>
      </div>
    </>
  );
}
